import React, { ReactNode } from 'react';
import Badge, { BadgeProps } from '../../../common/Badge/Badge';
import styles from './CourseCard.module.scss';
import { CY_COURSE_CARD_ATTRIBUTES } from '../../../../lib/constants/cypress';
import { Label } from '../../../common/Label/Label';
import { COURSE_CARDS_LABEL_KEYS, COMMON_LABELS } from '../../../../lib/constants/labelKeys';
import { getOverdueText } from '../../../../lib/utils/getOverdueText';
import { CourseCardProps } from './CourseCard.types';
import { isNil } from '../../../../lib/utils/isNil';
import { classes } from '../../../../lib/utils/cssUtils';
import { Api } from '../../../../lib/types/apiSpec';

type CardBadgeProps = Pick<CourseCardProps, 'dueDate' | 'status'> & {
  withNotStartedBadge?: boolean;
} & Omit<BadgeProps, 'children' | 'variant'>;

const progressToVariantMap = ({
  overdueText,
  status,
  withNotStartedBadge,
}: {
  overdueText?: ReactNode;
} & Pick<CardBadgeProps, 'status' | 'withNotStartedBadge'>): {
  variant: BadgeProps['variant'];
  content: ReactNode;
} | null => {
  switch (status) {
    case Api.TrainingStatus.COMPLETED:
      return {
        variant: 'success',
        content: <Label name={COURSE_CARDS_LABEL_KEYS.STATUS.COMPLETED} />,
      };
    case Api.TrainingStatus.OVERDUE: {
      return {
        variant: 'warn',
        content: overdueText,
      };
    }
    case Api.TrainingStatus.IN_PROGRESS: {
      return { variant: 'info', content: <Label name={COURSE_CARDS_LABEL_KEYS.STATUS.IN_PROGRESS} /> };
    }
    case Api.TrainingStatus.NOT_STARTED: {
      return withNotStartedBadge
        ? { variant: 'regular', content: <Label name={COURSE_CARDS_LABEL_KEYS.STATUS.NOT_STARTED} /> }
        : null;
    }
    default:
      return null;
  }
};

const CardBadge = ({ dueDate, status, className, withNotStartedBadge, ...rest }: CardBadgeProps) => {
  const overdueText = (() => {
    if (status !== Api.TrainingStatus.OVERDUE) {
      return null;
    }

    return dueDate ? (
      getOverdueText(dueDate, <Label className={styles.overdueLabel} name={COURSE_CARDS_LABEL_KEYS.STATUS.OVERDUE} />)
    ) : (
      <Label name={COMMON_LABELS.TRAINING.OVERDUE} />
    );
  })();

  const data = progressToVariantMap({
    overdueText,
    status,
    withNotStartedBadge,
  });

  if (isNil(data)) {
    return null;
  }

  return (
    <Badge
      role="status"
      className={classes(styles.badge, className)}
      data-cy={CY_COURSE_CARD_ATTRIBUTES.BADGE}
      variant={data.variant}
      {...rest}
    >
      {data.content}
    </Badge>
  );
};

export default CardBadge;
